import { DateTime } from '@ksyos/date-time';
import { orderBy } from 'lodash';
import type { CaroProcessActivity } from '@common/interfaces/caroProcessActivity';
import { CustomControlCode } from '@common/enums';

export const caroInactivityCustomControlCodes = [
    CustomControlCode.BariPostCaroInactivityPoliReport,
    CustomControlCode.BariPostCaroBodyWeightMeasurements,
    CustomControlCode.BariPostCaroBodyWeightMeasurementsInactiveNotification,
    CustomControlCode.BariPostCaroQuestionnaireResponse,
    CustomControlCode.BariPostCaroQuestionnaireResponsesInactiveNotification,
] as const;

type CaroInactivityCustomControlCode = (typeof caroInactivityCustomControlCodes)[number];

export function getBariPostCountsOfCaroInactivityReportsAndNotifications(
    activities: CaroProcessActivity[],
    untilDateTime = DateTime.now()
): {
    countOfInactivityReportsForBodyWeight: number;
    countOfInactivityReportsForQuestionnaires: number;
    countOfNotificationsForBodyWeight: number;
    countOfNotificationsForQuestionnaires: number;
} {
    const caroInactivityActivities: Record<CaroInactivityCustomControlCode, CaroProcessActivity[]> = {
        [CustomControlCode.BariPostCaroInactivityPoliReport]: [],
        [CustomControlCode.BariPostCaroBodyWeightMeasurements]: [],
        [CustomControlCode.BariPostCaroBodyWeightMeasurementsInactiveNotification]: [],
        [CustomControlCode.BariPostCaroQuestionnaireResponse]: [],
        [CustomControlCode.BariPostCaroQuestionnaireResponsesInactiveNotification]: [],
    };

    for (const customControlCode of caroInactivityCustomControlCodes) {
        caroInactivityActivities[customControlCode] = activities.filter(
            (activity) =>
                activity.serviceActivity.customControlCode === customControlCode &&
                DateTime.from(activity.performedDatetime).isBefore(untilDateTime)
        );
    }

    const {
        countOfInactivityReports: countOfInactivityReportsForBodyWeight,
        countOfNotifications: countOfNotificationsForBodyWeight,
    } = getCounts(
        caroInactivityActivities[CustomControlCode.BariPostCaroInactivityPoliReport],
        caroInactivityActivities[CustomControlCode.BariPostCaroBodyWeightMeasurements],
        caroInactivityActivities[CustomControlCode.BariPostCaroBodyWeightMeasurementsInactiveNotification]
    );

    const {
        countOfInactivityReports: countOfInactivityReportsForQuestionnaires,
        countOfNotifications: countOfNotificationsForQuestionnaires,
    } = getCounts(
        caroInactivityActivities[CustomControlCode.BariPostCaroInactivityPoliReport],
        caroInactivityActivities[CustomControlCode.BariPostCaroQuestionnaireResponse],
        caroInactivityActivities[CustomControlCode.BariPostCaroQuestionnaireResponsesInactiveNotification]
    );

    return {
        countOfInactivityReportsForBodyWeight,
        countOfInactivityReportsForQuestionnaires,
        countOfNotificationsForBodyWeight,
        countOfNotificationsForQuestionnaires,
    };
}

function getCounts(
    inactivityReports: CaroProcessActivity[],
    patientActivities: CaroProcessActivity[],
    inactivityNotifications: CaroProcessActivity[]
): { countOfInactivityReports: number; countOfNotifications: number } {
    const orderedActivities = orderBy(
        [
            ...inactivityNotifications.map((notification) => ({
                date: notification.performedDatetime,
                type: 'notification',
            })),
            ...inactivityReports.map((report) => ({
                date: report.performedDatetime,
                type: 'report',
            })),
            ...patientActivities.map((patientActivity) => ({
                date: patientActivity.performedDatetime,
                type: 'patientActivity',
            })),
        ],
        ['date'],
        ['desc']
    );

    let countOfInactivityReports = 0;
    let countOfNotifications = 0;

    // Count notifications and get index of earliest notification that was after the latest patientActivity
    let oldestNotificationIndex = 0;
    for (const [index, value] of orderedActivities.entries()) {
        if (value.type === 'notification') {
            oldestNotificationIndex = index;
            countOfNotifications++;
        } else if (value.type === 'patientActivity') {
            break;
        }
    }

    if (countOfNotifications > 0) {
        // Count inactivityReports after the earliest notification that was after the latest patientActivity
        for (let i = 0; i < oldestNotificationIndex; i++) {
            if (orderedActivities[i].type === 'report') {
                countOfInactivityReports++;
            }
        }
    }

    return { countOfInactivityReports, countOfNotifications };
}
